import React, { useState, useEffect, useRef } from 'react';
import { fetchOpenGraphData, OpenGraphData, isValidUrl } from '../services/openGraphService';

// Интерфейс компонента предпросмотра ссылок
interface LinkPreviewProps {
  url: string;
  position: { x: number; y: number };
  onClose: () => void;
  // Функция для удаления ссылки
  onRemoveLink?: () => void;
}

/**
 * Компонент предпросмотра ссылок с использованием OpenGraph
 * Отображает метаданные страницы, полученные по протоколу Open Graph
 */
const LinkPreview = ({ url, position, onClose, onRemoveLink }: LinkPreviewProps) => {
  const [previewData, setPreviewData] = useState<OpenGraphData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const previewRef = useRef<HTMLDivElement>(null);

  // Корректировка позиции, чтобы превью не выходило за границы экрана
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Проверка и коррекция позиции превью
  useEffect(() => {
    const adjustPosition = () => {
      // Не выполняем корректировку, если еще нет ссылки на DOM-элемент
      if (!previewRef.current) return;
      
      const previewRect = previewRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newX = position.x;
      let newY = position.y;
      
      // Проверяем, не выходит ли превью за правый край
      if (position.x + previewRect.width/2 > viewportWidth) {
        newX = viewportWidth - previewRect.width/2 - 10;
      }
      
      // Проверяем, не выходит ли превью за левый край
      if (position.x - previewRect.width/2 < 0) {
        newX = previewRect.width/2 + 10;
      }
      
      // Проверяем, не выходит ли превью за нижний край
      if (position.y + previewRect.height > viewportHeight + window.scrollY) {
        // Если выходит за нижний край, размещаем над ссылкой
        // Предполагаем, что высота ссылки примерно 20px
        newY = position.y - previewRect.height - 30; 
      }
      
      console.log('[LinkPreview] Корректировка позиции:', {
        original: position, 
        adjusted: {x: newX, y: newY},
        previewSize: {width: previewRect.width, height: previewRect.height}
      });
      
      // Обновляем позицию только если она изменилась
      if (newX !== adjustedPosition.x || newY !== adjustedPosition.y) {
        setAdjustedPosition({ x: newX, y: newY });
      }
    };
    
    // Первичная коррекция позиции
    setTimeout(adjustPosition, 50);
    
    // Корректируем позицию при изменении размера окна
    window.addEventListener('resize', adjustPosition);
    return () => window.removeEventListener('resize', adjustPosition);
  }, [position, adjustedPosition]);

  // Для отладки рендеринга компонента
  console.log('[LinkPreview] Рендер компонента для URL:', url, 'Состояние:', {
    isLoading,
    hasError: !!error,
    hasData: !!previewData,
    position: adjustedPosition
  });

  useEffect(() => {
    // Отслеживаем монтирование/размонтирование компонента
    console.log('[LinkPreview] Компонент смонтирован');
    isMounted.current = true;

    return () => {
      console.log('[LinkPreview] Компонент размонтирован');
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isValidUrl(url)) {
      console.log('[LinkPreview] Некорректный URL:', url);
      setError('Некорректный URL');
      setIsLoading(false);
      return;
    }

    console.log('[LinkPreview] Начинаем загрузку данных для URL:', url);
    // Получаем метаданные Open Graph
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('[LinkPreview] Вызов fetchOpenGraphData');
        const data = await fetchOpenGraphData(url);
        
        // Проверяем, что компонент все еще смонтирован
        if (isMounted.current) {
          console.log('[LinkPreview] Данные успешно получены:', data);
          setPreviewData(data);
          setError(null);
        } else {
          console.log('[LinkPreview] Компонент размонтирован до завершения запроса');
        }
      } catch (err) {
        if (isMounted.current) {
          console.error('[LinkPreview] Ошибка загрузки превью:', err);
          setError('Не удалось загрузить данные');
        }
      } finally {
        if (isMounted.current) {
          console.log('[LinkPreview] Завершение загрузки');
          setIsLoading(false);
        }
      }
    };

    fetchData();
    
    // Очистка эффекта
    return () => {
      console.log('[LinkPreview] Очистка эффекта для URL:', url);
    };
  }, [url]);

  // Получаем имя домена из URL для отображения
  const getDomainName = () => {
    try {
      return new URL(url).hostname;
    } catch {
      return 'Предпросмотр';
    }
  };

  // Если произошла ошибка при первоначальной валидации URL
  if (!isValidUrl(url)) {
    return (
      <div 
        className="link-preview-container"
        style={{
          position: 'absolute',
          left: `${adjustedPosition.x}px`,
          top: `${adjustedPosition.y}px`,
          zIndex: 100,
          transform: 'translateX(-50%)',
        }}
        ref={previewRef}
      >
        <div className="link-preview bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-72">
          <div className="p-4 flex items-center justify-center h-32">
            <div className="text-red-500">Некорректный URL</div>
          </div>
          <button className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full p-1" onClick={onClose}>×</button>
        </div>
        <div className="fixed inset-0 z-40" onClick={onClose} />
      </div>
    );
  }

  return (
    <div 
      className="link-preview-container"
      style={{
        position: 'fixed',
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        zIndex: 9999,
        transform: 'translateX(-50%)',
        pointerEvents: 'all',
      }}
      ref={previewRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="link-preview bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-72">
        {isLoading ? (
          <div className="p-4 flex items-center justify-center h-32">
            <div className="animate-pulse">Загрузка предпросмотра...</div>
          </div>
        ) : error ? (
          <div className="p-4 flex items-center justify-center h-32">
            <div className="text-red-500">{error}</div>
          </div>
        ) : (
          <>
            <a 
              href={url} 
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:no-underline"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            >
              {previewData?.image ? (
                <div className="w-full h-32 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <img 
                    src={previewData.image} 
                    alt={previewData.title} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log('[LinkPreview] Ошибка загрузки изображения:', previewData.image);
                      // Если изображение не загрузилось, отображаем заглушку
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.classList.add('bg-gradient-to-r', 'from-blue-100', 'to-indigo-100', 'dark:from-blue-900', 'dark:to-indigo-900', 'flex', 'items-center', 'justify-center');
                      const textDiv = document.createElement('div');
                      textDiv.className = 'text-blue-500 dark:text-blue-300 font-medium text-sm';
                      textDiv.textContent = getDomainName();
                      (e.target as HTMLImageElement).parentElement!.appendChild(textDiv);
                    }}
                  />
                </div>
              ) : (
                <div className="w-full h-32 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 overflow-hidden flex items-center justify-center">
                  <div className="text-blue-500 dark:text-blue-300 font-medium text-sm">
                    {getDomainName()}
                  </div>
                </div>
              )}
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 line-clamp-2">
                  {previewData?.title || 'Без заголовка'}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
                  {previewData?.description || 'Нет описания'}
                </p>
                {previewData?.siteName && (
                  <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {previewData.siteName}
                  </div>
                )}
                
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 truncate">
                  {url}
                </div>
                
                {/* Кнопки действий в одной строке */}
                <div className="mt-3 flex justify-between">
                  {onRemoveLink && (
                    <button 
                      className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 
                                text-gray-600 dark:text-gray-300 text-xs py-1 px-2 rounded-md transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRemoveLink();
                        onClose();
                      }}
                    >
                      Удалить ссылку
                    </button>
                  )}
                  <button 
                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-3 rounded-md transition-colors ml-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(url, '_blank');
                      onClose();
                    }}
                  >
                    Перейти
                  </button>
                </div>
              </div>
            </a>
          </>
        )}
        <button 
          className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
};

export default LinkPreview; 